import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import InfluencerProfileCompleteForm from 'Components/campaign/InfluencerProfileCompleteForm'
import Loader from 'Components/Common/Loader'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { resetCreatorsState } from 'slices/creators/reducer'
import { toggleUpdateCreatorProfileModal } from 'slices/ui/reducer'
import { fetchInfluencer } from 'services/api/influencer'
import { AppDispatch, RootState } from 'store'
import { Influencer } from 'types/influencer'
import { getErrorMessage } from 'utils/helpers'
import inModal from 'utils/hoc/inModal'
import './UpdateCreatorProfileModal.scss'
import { fetchAllCollaborationsThunk } from 'slices/collaborations/thunk'

interface UpdateCreatorProfileModalProps {
    isActive: boolean
    onClose: () => void
    influencerId?: string
}

const UpdateCreatorProfileModal: React.FC<UpdateCreatorProfileModalProps> = ({
    isActive,
    onClose,
    influencerId,
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const hubId = useSelector((state: RootState) => state.auth.selectedHubId)
    const filters = useSelector((state: RootState) => state.creators.filters)

    const [influencer, setInfluencer] = useState<Influencer | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!isActive || !hubId || !influencerId) {
            setInfluencer(null)
            return
        }

        let cancelled = false
        setLoading(true)
        setInfluencer(null)

        fetchInfluencer(hubId, influencerId)
            .then((data) => {
                if (!cancelled) setInfluencer(data)
            })
            .catch((error) => {
                if (!cancelled) {
                    toast.error(getErrorMessage(error, 'Failed to load creator profile.'))
                    dispatch(toggleUpdateCreatorProfileModal(null))
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [isActive, hubId, influencerId, dispatch])

    const handleUpdated = (updated: Influencer) => {
        setInfluencer(updated)
        window.location.reload()
    }

    if (!isActive) return null

    if (loading) {
        return (
            <div className="update-creator-profile-modal__loader">
                <Loader />
            </div>
        )
    }

    if (!hubId || !influencer) {
        return (
            <div className="update-creator-profile-modal__empty text-muted">
                Unable to load creator profile.
            </div>
        )
    }

    return (
        <InfluencerProfileCompleteForm
            hubId={hubId}
            influencer={influencer}
            onUpdated={handleUpdated}
        />
    )
}

export default inModal(UpdateCreatorProfileModal, 'Creator Profile', {
    id: 'update-creator-profile-modal',
    dialogClassName: 'update-creator-profile-modal-dialog',
    modalClassName: 'update-creator-profile-modal',
    modalBodyClassName: 'p-0',
    modalHeaderClassName: 'border-bottom',
    scrollable: true,
})
